/**
 * Compliance Center - Phase 8 batch A (2026-05-16).
 *
 * The "compliance command center" view a regulated-industry evaluator
 * (DPO, Compliance Analyst, Legal Reviewer) expects on first login.
 * Three tabs:
 *   • Coverage - per-regulation green/amber/red rollup with control drill-down
 *   • Gaps     - actionable list of every non-green control
 *   • Export   - generate a signed snapshot (JSON / CSV / HTML)
 *
 * All data aggregates from already-existing surfaces - no new article
 * workflows. Batches B-G land the 14+ GDPR/DPDPA article state machines.
 */
import { Rx, useRegulationText } from '@/lib/regulation-terms';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from '@tanstack/react-router';
import { motion, AnimatePresence } from 'motion/react';
import {
  ShieldCheck,
  AlertTriangle,
  XCircle,
  HelpCircle,
  CheckCircle2,
  CircleDashed,
  RefreshCw,
  Loader2,
  Download,
  Filter,
  FileText,
  ListChecks,
  ExternalLink,
  Code2,
} from 'lucide-react';
import { GlassCard, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { PageContainer, PageHeader } from '@/components/viz/PageHeader';
import { ErrorState, LoadingState } from '@foctta/ui-shell';
import { KpiCard } from '@/components/viz/KpiCard';
import {
  getComplianceCoverage,
  getComplianceGaps,
  buildComplianceExport,
  type ComplianceCoverageResponse,
  type ComplianceGapsResponse,
  type ComplianceStatus,
  type ComplianceExportFormat,
} from '@/lib/api';

const STATUS_VARIANT: Record<ComplianceStatus, 'success' | 'warning' | 'critical' | 'glass'> = {
  green: 'success',
  amber: 'warning',
  red: 'critical',
  not_started: 'glass',
  unknown: 'glass',
};

const STATUS_ICON: Record<ComplianceStatus, typeof CheckCircle2> = {
  green: CheckCircle2,
  amber: AlertTriangle,
  red: XCircle,
  not_started: CircleDashed,
  unknown: HelpCircle,
};

// Human label for a status chip (so `not_started` reads "Not started", not "NOT_STARTED").
const STATUS_LABEL: Record<ComplianceStatus, string> = {
  green: 'green',
  amber: 'amber',
  red: 'red',
  not_started: 'not started',
  unknown: 'unknown',
};

const SEVERITY_VARIANT: Record<'critical' | 'high' | 'medium' | 'low', 'critical' | 'warning' | 'glass' | 'glass'> = {
  critical: 'critical',
  high: 'warning',
  medium: 'glass',
  low: 'glass',
};

function downloadAsFile(name: string, contents: string, contentType: string): void {
  const blob = new Blob([contents], { type: contentType });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = name;
  a.click();
  URL.revokeObjectURL(url);
}

export function ComplianceCenter(): JSX.Element {
  const tx = useRegulationText();
  const navigate = useNavigate();
  const [tab, setTab] = useState<'coverage' | 'gaps' | 'export'>('coverage');
  const [coverage, setCoverage] = useState<ComplianceCoverageResponse | null>(null);
  const [gaps, setGaps] = useState<ComplianceGapsResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [expandedReg, setExpandedReg] = useState<string | null>(null);
  const [showApiDocs, setShowApiDocs] = useState(false);

  // Export form state
  const [exportFormat, setExportFormat] = useState<ComplianceExportFormat>('json');
  const [exportRegulation, setExportRegulation] = useState<string>('all');
  const [exportBusy, setExportBusy] = useState(false);
  const [lastExport, setLastExport] = useState<{ digest: string; rowCount: number; at: string } | null>(null);

  const refresh = useCallback(async (): Promise<void> => {
    setLoading(true);
    setError(null);
    try {
      const [c, g] = await Promise.all([getComplianceCoverage(), getComplianceGaps()]);
      setCoverage(c);
      setGaps(g);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'failed to load compliance data');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { void refresh(); }, [refresh]);

  async function handleExport(): Promise<void> {
    setExportBusy(true);
    setError(null);
    try {
      const r = await buildComplianceExport({
        format: exportFormat,
        ...(exportRegulation !== 'all' ? { regulationId: exportRegulation as never } : {}),
      });
      const ext = r.format === 'json' ? 'json' : r.format === 'csv' ? 'csv' : 'html';
      const regSuffix = r.regulationId ? `-${r.regulationId}` : '';
      downloadAsFile(`foctta-compliance${regSuffix}-${new Date().toISOString().slice(0, 10)}.${ext}`, r.body, r.contentType);
      setLastExport({ digest: r.signedDigest, rowCount: r.rowCount, at: r.generatedAt });
    } catch (e) {
      setError(e instanceof Error ? e.message : 'export failed');
    } finally {
      setExportBusy(false);
    }
  }

  const overallPct = coverage ? Math.round(coverage.overall.score * 100) : 0;

  return (
    <PageContainer>
      <PageHeader
        title="Compliance Center"
        subtitle="One view of where this tenant stands against DPDPA · GDPR · CCPA · RBI. Coverage, gaps, and signed exports for the DPO."
        badges={[
          { label: 'DPO command center', variant: 'glass' },
          /*
           * Backlog item 26. The subtitle used to read
           * "DPDPA · GDPR · CCPA · RBI · IRDAI · SEBI" beside a hardcoded
           * "6 regulatory regimes" badge, while the KPI card below already rendered
           * the real figure from `coverage.regulations.length`. That is the
           * "6 in one place, 5 in another" that was reported.
           *
           * The compliance service defines exactly five control sets - DPDPA_2023,
           * DPDPA_S9, GDPR, CCPA, RBI_MD_Cyber - pushed unconditionally, so the count
           * is not tenant-dependent. IRDAI and SEBI appear elsewhere in the codebase
           * (retention, audit vault) but have NO control set here, so naming them on
           * this page claimed regulatory coverage the page cannot show - a worse
           * problem than the count being wrong, especially for a BFSI reader.
           *
           * Derived, not re-hardcoded, so adding a control set cannot desync it again.
           * Labelled "control sets" rather than "regimes" because DPDPA contributes
           * two of the five: the 2023 umbrella and the S.9 child-data set.
           */
          ...(coverage
            ? [
                {
                  label: `${coverage.regulations.length} control set${coverage.regulations.length === 1 ? '' : 's'}`,
                  variant: 'glass' as const,
                },
              ]
            : []),
          ...(loading
            ? [{ label: 'Loading', variant: 'glass' as const }]
            : error
              ? [{ label: 'Load error', variant: 'warning' as const }]
              : coverage
                ? [{ label: `${overallPct}% overall`, variant: STATUS_VARIANT[coverage.overall.status] as 'success' | 'warning' | 'critical' | 'glass' }]
                : []),
        ]}
        actions={
          <div className="flex gap-2">
            <Button size="sm" variant="glass" onClick={() => setShowApiDocs((v) => !v)}>
              <Code2 className="h-3.5 w-3.5 mr-1" />
              {tx(showApiDocs ? 'Hide DSAR API' : 'Bulk DSAR API')}
            </Button>
            <Button size="sm" variant="glass" onClick={() => void refresh()} disabled={loading}>
              <RefreshCw className={`h-3.5 w-3.5 mr-1 ${loading ? 'animate-spin' : ''}`} />
              Refresh
            </Button>
          </div>
        }
      />

      {showApiDocs && (
        <GlassCard className="p-5 mb-6 border-primary/40 glow-low">
          <CardHeader className="p-0 mb-3">
            <CardTitle className="flex items-center gap-2">
              <Code2 className="h-4 w-4 text-primary" />
              <Rx>Bulk DSAR API - programmatic access</Rx>
            </CardTitle>
            <CardDescription>
              For DPO teams importing principal lookups from CRM forms, intake portals, or
              SIEM. All endpoints require an active <code>foctta_platform</code> license and a
              JWT with <code>discovery:read</code> (or <code>discovery:write</code> for
              ingest). RLS enforces tenant isolation at the database layer.
            </CardDescription>
          </CardHeader>
          <div className="space-y-3 text-xs">
            <ApiEndpoint
              method="POST"
              path="/v1/discovery/reverse-dsar"
              desc="Locate every column/table where a given principal identifier appears across all scanned sources."
              body={`{\n  "principalIdentifier": "<email | pan | aadhaar-last-4 | customer-id>",\n  "identifierKind": "email" | "pan" | "aadhaar_last_4" | "customer_id" | "custom"\n}`}
            />
            <ApiEndpoint
              method="POST"
              path="/v1/discovery/reverse-dsar/:id/export"
              desc={tx("Export a previously-resolved reverse-DSAR result as a signed JSON evidence pack.")}
              body={`{\n  "format": "json" | "csv"\n}`}
            />
            <ApiEndpoint
              method="POST"
              path="/v1/compliance/erasure"
              desc="Submit a DPDPA / GDPR right-to-erasure request. Async - returns a job id; poll /erasure/:id for completion."
              body={`{\n  "principalIdentifier": "<id>",\n  "regulation": "dpdpa" | "gdpr" | "ccpa",\n  "justification": "<free text>"\n}`}
            />
            <ApiEndpoint
              method="POST"
              path="/v1/compliance/portability"
              desc="Generate a portability bundle (Article 20 GDPR · DPDPA Sec 11). Returns a download token + KMS-wrapped export."
              body={`{\n  "principalIdentifier": "<id>",\n  "format": "json" | "csv"\n}`}
            />
            <div className="glass-lite p-3 rounded-md mt-2">
              <div className="text-[10px] uppercase text-muted-foreground mb-1">Bulk import pattern</div>
              <p className="text-[11px] leading-snug text-muted-foreground">
                <Rx>For 50+ DSARs from a CRM webform</Rx>: POST each principal to{' '}
                <code className="font-mono">/v1/discovery/reverse-dsar</code> in parallel
                (Idempotency-Key header recommended for retries - cached 24h in Redis).
                Discovery rate-limit: 50 req/s per tenant. Each call returns within p95
                ~140ms for a tenant with &lt; 200 sources.
              </p>
            </div>
          </div>
        </GlassCard>
      )}

      {error && (
        <ErrorState
          title="Failed to load compliance data"
          detail={error}
          onRetry={() => void refresh()}
        />
      )}

      {coverage && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-6">
          <KpiCard label="Overall score" value={coverage.overall.score} format="percent" icon={ShieldCheck} glow={coverage.overall.status === 'green' ? 'high' : coverage.overall.status === 'amber' ? 'medium' : 'critical'} hint={`${coverage.regulations.length} regulations`} />
          <KpiCard label="Green" value={coverage.overall.greenRegulations} icon={CheckCircle2} glow={coverage.overall.greenRegulations > 0 ? 'high' : 'low'} hint="fully covered" />
          <KpiCard label="Amber" value={coverage.overall.amberRegulations} icon={AlertTriangle} glow={coverage.overall.amberRegulations > 0 ? 'medium' : 'low'} hint="strengthen" />
          <KpiCard label="Red" value={coverage.overall.redRegulations} icon={XCircle} glow={coverage.overall.redRegulations > 0 ? 'critical' : 'low'} hint="remediate" />
          {/* Release 2.1 item 4: regulations not begun or not assessable used to
              appear in no card, so the four cards did not add up to the total. */}
          <KpiCard label="Not started" value={coverage.overall.notStartedRegulations + coverage.overall.unknownRegulations} icon={CircleDashed} glow="low" hint="no evidence yet" />
        </div>
      )}

      {/* Tabs */}
      <div className="flex flex-wrap gap-2 mb-4">
        {([
          ['coverage', 'Coverage', ShieldCheck],
          ['gaps', 'Gaps', ListChecks],
          ['export', 'Export', FileText],
        ] as const).map(([key, label, Icon]) => (
          <button
            key={key}
            type="button"
            onClick={() => setTab(key)}
            className={`px-3 py-1.5 rounded-full text-xs font-medium inline-flex items-center gap-1.5 transition-colors ${
              tab === key
                ? 'bg-primary text-primary-foreground'
                : 'glass-lite hover:bg-muted/30'
            }`}
          >
            <Icon className="h-3 w-3" />
            {label}
            {key === 'gaps' && gaps && gaps.gaps.length > 0 && (
              <span className="ml-1 text-[10px] opacity-75 tabular">({gaps.gaps.length})</span>
            )}
          </button>
        ))}
      </div>

      <AnimatePresence mode="wait">
        {/* Coverage tab */}
        {tab === 'coverage' && coverage && (
          <motion.div
            key="coverage"
            initial={{ opacity: 0, y: 4 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -4 }}
            className="space-y-3"
          >
            {coverage.regulations.map((reg, i) => {
              const StatusIcon = STATUS_ICON[reg.status];
              const expanded = expandedReg === reg.regulationId;
              return (
                <motion.div
                  key={reg.regulationId}
                  initial={{ opacity: 0, x: -4 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: Math.min(i * 0.03, 0.3) }}
                >
                  <GlassCard className="p-4">
                    <button
                      type="button"
                      className="w-full flex items-start justify-between gap-3 text-left"
                      onClick={() => setExpandedReg(expanded ? null : reg.regulationId)}
                    >
                      <div className="flex items-start gap-3 min-w-0">
                        <StatusIcon className={`h-5 w-5 shrink-0 mt-0.5 ${
                          reg.status === 'green' ? 'text-success'
                            : reg.status === 'amber' ? 'text-warning'
                            : reg.status === 'red' ? 'text-sev-critical'
                            : 'text-muted-foreground'
                        }`} />
                        <div className="min-w-0">
                          <div className="text-sm font-medium flex items-center gap-2 flex-wrap">
                            {reg.displayName}
                            <Badge variant="glass" className="text-[10px]">{reg.jurisdiction}</Badge>
                            <Badge variant={STATUS_VARIANT[reg.status]} className="text-[10px]">
                              {STATUS_LABEL[reg.status].toUpperCase()}
                            </Badge>
                          </div>
                          <div className="text-[11px] text-muted-foreground mt-0.5">
                            Score {(reg.coverageScore * 100).toFixed(0)}% · {reg.controlsTotal} control(s):
                            {' '}<span className="text-success">{reg.controlsGreen} green</span> ·
                            {' '}<span className="text-warning">{reg.controlsAmber} amber</span> ·
                            {' '}<span className="text-sev-critical">{reg.controlsRed} red</span>
                            {reg.controlsNotStarted + reg.controlsUnknown > 0 && (
                              <> · <span>{reg.controlsNotStarted + reg.controlsUnknown} not started</span></>
                            )}
                          </div>
                        </div>
                      </div>
                      <div className="text-xs text-muted-foreground shrink-0">
                        {expanded ? '▲' : '▼'}
                      </div>
                    </button>

                    {expanded && (
                      <motion.div
                        initial={{ opacity: 0, height: 0 }}
                        animate={{ opacity: 1, height: 'auto' }}
                        exit={{ opacity: 0, height: 0 }}
                        className="mt-3 pt-3 border-t border-border/20 space-y-2"
                      >
                        {reg.controls.map((c) => {
                          const CIcon = STATUS_ICON[c.status];
                          return (
                            <div key={c.id} className="glass-lite rounded p-2 text-xs flex items-start gap-2">
                              <CIcon className={`h-3.5 w-3.5 shrink-0 mt-0.5 ${
                                c.status === 'green' ? 'text-success'
                                  : c.status === 'amber' ? 'text-warning'
                                  : c.status === 'red' ? 'text-sev-critical'
                                  : 'text-muted-foreground'
                              }`} />
                              <div className="flex-1 min-w-0">
                                <div className="font-medium flex items-center gap-2 flex-wrap">
                                  {c.name}
                                  <Badge variant={STATUS_VARIANT[c.status]} className="text-[9px]">{STATUS_LABEL[c.status]}</Badge>
                                </div>
                                <div className="text-muted-foreground mt-0.5"><Rx>{c.detail}</Rx></div>
                              </div>
                              {c.sourceRef && (
                                <Button size="sm" variant="glass" onClick={() => navigate({ to: c.sourceRef as never })} title="Open source surface">
                                  <ExternalLink className="h-3 w-3" />
                                </Button>
                              )}
                            </div>
                          );
                        })}
                      </motion.div>
                    )}
                  </GlassCard>
                </motion.div>
              );
            })}
          </motion.div>
        )}

        {/* Gaps tab */}
        {tab === 'gaps' && gaps && (
          <motion.div
            key="gaps"
            initial={{ opacity: 0, y: 4 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -4 }}
            className="space-y-3"
          >
            <div className="grid grid-cols-4 gap-3 text-xs">
              {([
                ['critical', 'Critical', gaps.bySeverity.critical, 'text-sev-critical'],
                ['high', 'High', gaps.bySeverity.high, 'text-warning'],
                ['medium', 'Medium', gaps.bySeverity.medium, 'text-muted-foreground'],
                ['low', 'Low', gaps.bySeverity.low, 'text-muted-foreground'],
              ] as const).map(([key, label, count, color]) => (
                <div key={key} className="glass-lite rounded p-3">
                  <div className="text-muted-foreground mb-0.5">{label}</div>
                  <div className={`tabular text-2xl font-medium ${color}`}>{count}</div>
                </div>
              ))}
            </div>

            {gaps.gaps.length === 0 ? (
              <GlassCard className="p-8 text-center text-sm text-muted-foreground">
                <CheckCircle2 className="h-8 w-8 mx-auto mb-3 text-success" />
                <div className="font-medium text-foreground/90">No compliance gaps</div>
                <div className="mt-1">All controls are green. Nice.</div>
              </GlassCard>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-xs tabular">
                  <thead>
                    <tr className="text-left text-muted-foreground border-b border-border/30">
                      <th className="py-2 pr-3 font-normal">Severity</th>
                      <th className="py-2 pr-3 font-normal">Regulation</th>
                      <th className="py-2 pr-3 font-normal">Control</th>
                      <th className="py-2 pr-3 font-normal">Recommended action</th>
                      <th className="py-2 pr-3 font-normal">Owner</th>
                      <th />
                    </tr>
                  </thead>
                  <tbody>
                    {gaps.gaps.map((g, i) => (
                      <motion.tr
                        key={g.id}
                        initial={{ opacity: 0, x: -4 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ delay: Math.min(i * 0.02, 0.4) }}
                        className="border-b border-border/15 hover:bg-muted/20 align-top"
                      >
                        <td className="py-2 pr-3 whitespace-nowrap">
                          <Badge variant={SEVERITY_VARIANT[g.severity]} className="text-[10px]">
                            {g.severity}
                          </Badge>
                        </td>
                        <td className="py-2 pr-3 whitespace-nowrap font-mono text-[10px]">{g.regulationId}</td>
                        <td className="py-2 pr-3">
                          <div className="font-medium">{g.title}</div>
                          <div className="text-muted-foreground text-[10px] mt-0.5">{g.detail}</div>
                        </td>
                        <td className="py-2 pr-3">{g.recommendedAction}</td>
                        <td className="py-2 pr-3 whitespace-nowrap">
                          <Badge variant="glass" className="text-[10px]">{g.ownerRoleHint}</Badge>
                        </td>
                        <td className="py-2 pr-3 whitespace-nowrap">
                          {g.sourceRoute && (
                            <Button size="sm" variant="glass" onClick={() => navigate({ to: g.sourceRoute as never })} title="Open">
                              <ExternalLink className="h-3 w-3" />
                            </Button>
                          )}
                        </td>
                      </motion.tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </motion.div>
        )}

        {/* Export tab */}
        {tab === 'export' && (
          <motion.div
            key="export"
            initial={{ opacity: 0, y: 4 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -4 }}
            className="space-y-3"
          >
            <GlassCard className="p-5">
              <CardHeader className="p-0 mb-3">
                <CardTitle>Generate compliance export</CardTitle>
                <CardDescription clamp>
                  Bundle the current Coverage + Gaps + recent Findings snapshot. SHA-256 signed; the
                  digest goes into the audit chain so a regulator can verify what was exported.
                </CardDescription>
              </CardHeader>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs text-muted-foreground block mb-0.5">Format</label>
                  <select
                    value={exportFormat}
                    onChange={(e) => setExportFormat(e.target.value as ComplianceExportFormat)}
                    className="w-full glass-lite text-sm rounded px-3 py-2 bg-background/40 border border-border/40"
                  >
                    <option value="json">JSON (canonical)</option>
                    <option value="csv">CSV (gaps table)</option>
                    <option value="pdf">HTML (PoC; real PDF post-PoC)</option>
                  </select>
                </div>
                <div>
                  <label className="text-xs text-muted-foreground block mb-0.5">Regulation filter</label>
                  <select
                    value={exportRegulation}
                    onChange={(e) => setExportRegulation(e.target.value)}
                    className="w-full glass-lite text-sm rounded px-3 py-2 bg-background/40 border border-border/40"
                  >
                    <option value="all">All regulations</option>
                    {coverage?.regulations.map((r) => (
                      <option key={r.regulationId} value={r.regulationId}>{r.displayName}</option>
                    ))}
                  </select>
                </div>
              </div>
              <div className="flex justify-end mt-4">
                <Button size="sm" data-action="disc.comp_center.a01" onClick={() => void handleExport()} disabled={exportBusy}>
                  {exportBusy
                    ? <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" />
                    : <Download className="h-3.5 w-3.5 mr-1" />}
                  Generate + download
                </Button>
              </div>
            </GlassCard>

            {lastExport && (
              <GlassCard className="p-4 border border-success/40">
                <CardHeader className="p-0 mb-2">
                  <CardTitle className="text-sm flex items-center gap-2">
                    <CheckCircle2 className="h-4 w-4 text-success" />
                    Export generated
                  </CardTitle>
                </CardHeader>
                <div className="text-xs space-y-1">
                  <div>Generated at <code className="font-mono">{new Date(lastExport.at).toLocaleString()}</code></div>
                  <div>{lastExport.rowCount.toLocaleString('en-IN')} row(s) bundled</div>
                  <div className="break-all">SHA-256: <code className="font-mono text-[10px]">{lastExport.digest}</code></div>
                </div>
              </GlassCard>
            )}
          </motion.div>
        )}
      </AnimatePresence>

      {loading && !coverage && (
        <LoadingState caption="Loading compliance data…" />
      )}
    </PageContainer>
  );
}

function ApiEndpoint({
  method,
  path,
  desc,
  body,
}: {
  method: 'GET' | 'POST' | 'PUT' | 'DELETE';
  path: string;
  desc: string;
  body?: string;
}): JSX.Element {
  const methodColor: Record<string, string> = {
    GET: 'text-success',
    POST: 'text-primary',
    PUT: 'text-warning',
    DELETE: 'text-sev-critical',
  };
  return (
    <div className="glass-lite p-3 rounded-md">
      <div className="flex items-center gap-3 mb-1">
        <span className={`font-mono text-[11px] font-semibold ${methodColor[method] ?? ''}`}>{method}</span>
        <code className="font-mono text-xs">{path}</code>
      </div>
      <p className="text-[11px] text-muted-foreground leading-snug">{desc}</p>
      {body && (
        <pre className="mt-2 font-mono text-[10px] bg-muted/30 p-2 rounded overflow-x-auto whitespace-pre">
{body}
        </pre>
      )}
    </div>
  );
}
